/**
 * FormField Adapter for shadcn
 * 
 * This component adapts form field patterns to fit our design system.
 */
import React from 'react';

// Import the shadcn Form components if available
// Or use our own implementation
import { cn } from '@/lib/utils';

// Define the FormField interface based on our design system
export interface FormFieldProps {
  label?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  id?: string;
}

/**
 * FormField adapter
 *
 * This component provides a consistent layout for form fields.
 * 
 * @example
 * ```tsx
 * <FormField 
 *   label="Email"
 *   helperText="We'll never share your email"
 *   required
 * >
 *   <Input type="email" />
 * </FormField>
 * ```
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  helperText,
  error,
  required,
  disabled,
  className,
  children,
  id,
}) => {
  // Generate a unique ID if not provided
  const fieldId = id || React.useId();
  
  // Clone the child to pass the id and aria attributes
  const childElement = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, {
        id: fieldId,
        'aria-describedby': helperText || error ? `${fieldId}-description` : undefined,
        'aria-invalid': !!error,
        disabled,
        ...child.props
      });
    }
    return child;
  });

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label 
          htmlFor={fieldId}
          className={cn(
            'block text-sm font-medium',
            error ? 'text-red-500' : 'text-gray-700',
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div>
        {childElement}
      </div>
      
      {(helperText || error) && (
        <p 
          id={`${fieldId}-description`}
          className={cn(
            'text-sm',
            error ? 'text-red-500' : 'text-gray-500'
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
};

FormField.displayName = 'FormField';
